import { forwardRef } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/cn";

/**
 * Tabs — Radix asosida.
 * Faza 2 use-case: Tenant Settings sahifasi (General/Branding/Members/Invitations/Plan/Danger)
 *
 * Variantlar:
 *   - default: bottom-border underline (Linear/GitHub stilida)
 *   - pills: pill chip variant (compact filterlar uchun)
 *   - vertical: chap sidebar (Settings layout'ida)
 *
 * Active indicator — Aurora gradient (default va pills'da)
 */
export const Tabs = TabsPrimitive.Root;

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: "default" | "pills" | "vertical";
}

export const TabsList = forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = "default", ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex items-center",
      variant === "default" &&
        "h-10 gap-1 border-b border-[var(--border-subtle)] w-full",
      variant === "pills" &&
        "h-9 gap-1 rounded-lg bg-[var(--bg-surface)] p-1 border border-[var(--border-subtle)]",
      variant === "vertical" && "flex-col gap-1 items-stretch w-full",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = "TabsList";

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: "default" | "pills" | "vertical";
}

export const TabsTrigger = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = "default", ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center gap-1.5 text-sm font-medium transition-all",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/60",
      "disabled:pointer-events-none disabled:opacity-50",
      // Default: underline bottom
      variant === "default" && [
        "h-10 px-3 -mb-px border-b-2 border-transparent",
        "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
        "data-[state=active]:text-[var(--text-primary)]",
        "data-[state=active]:border-[var(--accent-violet)]",
      ],
      // Pills
      variant === "pills" && [
        "h-7 px-3 rounded-md",
        "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
        "data-[state=active]:bg-[var(--bg-elevated)] data-[state=active]:text-[var(--text-primary)]",
        "data-[state=active]:shadow-[var(--shadow-subtle)]",
      ],
      // Vertical (Settings sidebar)
      variant === "vertical" && [
        "h-9 px-3 rounded-md justify-start",
        "text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]",
        "data-[state=active]:bg-[var(--bg-surface)] data-[state=active]:text-[var(--text-primary)]",
        "data-[state=active]:border-l-2 data-[state=active]:border-[var(--accent-violet)] data-[state=active]:pl-[10px]",
      ],
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-4 focus-visible:outline-none",
      "data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-150",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";
